import { JSONSchemaType } from 'ajv';
import { TDateFormat } from '../components/inputs/dateInput.tsx';
import { TDynamicStoreField } from '../store/store';

export enum Alert {
  Info = 'info',
  Success = 'success',
  Warning = 'warning',
  Time = 'time',
}

export enum FieldType {
  Callout = 'callout',
  Input = 'input',
  Card = 'card',
  Text = 'text',
  Selector = 'selector',
  CountrySelector = 'countrySelector',
  ProvinceSelector = 'provinceSelector',
  SelectorDrawer = 'selectorDrawer',
  WelcomeOffer = 'welcomeOffer',
  PhoneNumberInput = 'phoneNumberInput',
  EnterAddressManuallyText = 'enterAddressManually',
  EmailInput = 'emailInput',
  AutoCompleteAddressInput = 'autoCompleteAddressInput',
  DateInput = 'dateInput',
  ConsentField = 'consentField',
  WoCallout = 'woCallout',
  DepositIncrementer = 'depositIncrementer',
  DepositTypeSelector = 'depositTypeSelector',
  PostcodeInput = 'postcodeInput',
  DepositLimits = 'depositLimits',
  IdentificationNumberES = 'identificationNumberES',
  IdentityProtectedPassword = 'identityProtectedPassword',
  MultiMarketingConsent = 'multiMarketingConsent',
  PasswordInput = 'passwordInput',
  EsPasswordInput = 'esPasswordInput',
}

export enum ButtonType {
  Login = 'loginButton',
  Redirect = 'redirectButton',
  SecondaryRedirect = 'secondaryRedirectButton',
  DepositLimits = 'depositLimitsButton',
  NoBonus = 'noBonusButton',
  CountrySelector = 'countrySelectorButton',
  SecondaryDepositLimits = 'secondaryDepositLimitsButton',
  RegisterPrimary = 'registerPrimaryButton',
  RegisterSecondary = 'registerSecondaryButton',
  ReviewDetails = 'reviewDetails',
  AddressButton = 'addressButton',
  MultiMarketingConsent = 'multiMarketingConsent',
  Deposit = 'depositButton',
}

export type TDependsOn = {
  field: string;
  booleanValue?: boolean;
  value?: string;
}

export type TBaseField = {
  name: string;
}

export type TCalloutField = {
  fieldType: FieldType.Callout;
  label: string;
  description: string;
  dependsOn?: TDependsOn[];
  alert: Alert;
} & TBaseField;

export type TCardField = {
  fieldType: FieldType.Card;
  label: string;
  description?: string;
  fields: TField[];
  dependsOn?: TDependsOn[];
} & TBaseField;

export type TInputField = {
  fieldType: FieldType.Input;
  label?: string;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  flex?: boolean;
  clearable?: boolean;
  dependsOn?: TDependsOn[];
  validation?: JSONSchemaType<unknown>;
  gtmPayload?: Record<string, string>;
} & TBaseField;

export type TSelectorField = {
  fieldType: FieldType.Selector;
  label: string;
  validation?: JSONSchemaType<unknown>;
  options?: {
    value: string;
    label: string;
  }[];
} & TBaseField;

export type TTextField = {
  fieldType: FieldType.Text;
  label: string;
  textAlign?: string;
  size?: string;
  fontWeight?: string;
  color?: string;
  storeCondition?: string;
  dependsOn?: TDependsOn[];
} & TBaseField & Partial<HTMLParagraphElement>;

export type TEnterAddressManuallyTextField = {
  fieldType: FieldType.EnterAddressManuallyText;
  label: string;
  link: string;
  toggleEnterAddressManually: boolean;
  dependsOn?: TDependsOn[];
  styles?: { label: string; link: string };
} & TBaseField;

export type TCountrySelectorField = {
  fieldType: FieldType.CountrySelector;
  label: string;
  dialogTitle: string;
  placeholder?: string;
  disabled?: boolean;
  validation?: JSONSchemaType<unknown>;
  dependsOn?: TDependsOn[];
} & TBaseField;

export type TProvinceSelectorField = {
  fieldType: FieldType.ProvinceSelector;
  label: string;
  dialogTitle: string;
  placeholder?: string;
  disabled?: boolean;
  validation?: JSONSchemaType<unknown>;
  dependsOn?: TDependsOn[];
} & TBaseField;

export type TSelectorDrawerField = {
  fieldType: FieldType.SelectorDrawer;
  label: string;
  dialogTitle: string;
  placeholder?: string;
  disabled?: boolean;
  validation?: JSONSchemaType<unknown>;
  dependsOn?: TDependsOn[];
} & TBaseField;

export type TPhoneNumberInputField = {
  fieldType: FieldType.PhoneNumberInput;
  label: string;
  dialogTitle: string;
  validation?: JSONSchemaType<unknown>;
  gtmPayload?: Record<string, string>;
} & TBaseField;

export type TMultiMarketingConsentField = {
  fieldType: FieldType.MultiMarketingConsent;
  label: string;
  dialogTitle: string;
  validation?: JSONSchemaType<unknown>;
  gtmPayload?: Record<string, string>;
  title: string;
  description: string;
  casinoLabel: string;
  sportsLabel: string;
  emailLabel: string;
  smsLabel: string;
  phoneLabel: string;
  postLabel: string;
  pushLabel: string;
  expandText: string;
  closeText: string;
  explanatoryText: string;
  // Newly added translations
  verticalError?: string;
  verticalWarning?: string;
  contactError?: string;
  contactWarning?: string;
  verticalTitle?: string;
  contactTitle?: string;
  verticalSubtitle?: string;
} & TBaseField;

export type TEmailInputField = {
  fieldType: FieldType.EmailInput;
  label: string;
  dialogTitle: string;
  dialogDescription: string;
  primaryButtonTitle: string;
  secondaryButtonTitle: string;
  clearsPasswordOnClear?: boolean;
  autoFocused?: boolean;
  validation?: JSONSchemaType<unknown>;
} & TBaseField;

export type TAutoCompleteAddressInput = {
  fieldType: FieldType.AutoCompleteAddressInput;
  label: string;
  dialogTitle?: string;
  validation?: JSONSchemaType<unknown>;
} & TBaseField;

export type TWelcomeOfferField = {
  fieldType: FieldType.WelcomeOffer;
  name: string;
} & TBaseField;

export type TDateInputField = {
  fieldType: FieldType.DateInput;
  label: string;
  dateFormat?: TDateFormat;
  minAge?: number;
} & TBaseField;

export type TConsentField = {
  fieldType: FieldType.ConsentField;
  label: string;
  description: string | null;
  consents: {
    name: string;
    replaceWith: string;
    staticTermsName: string;
    link: string;
  }[];
  textAlign?: string;
  validation?: JSONSchemaType<unknown>;
  isCheckable?: boolean;
  title?: string;
} & TBaseField;

export type TWoCalloutField = {
  fieldType: FieldType.WoCallout;
  label: string;
  description: string;
  alert: Alert;
  dependsOn?: TDependsOn[];
  dialogTitle?: string;
  noBonusSelect?: string;
  bonusSelect?: string;
  token?: string;
} & TBaseField;

export type TDepositIncrementerField = {
  fieldType: FieldType.DepositIncrementer;
  label: string;
  options?: {
    value: string;
    label: string;
  }[];
} & TBaseField;

export type TDepositTypeSelectorField = {
  fieldType: FieldType.DepositTypeSelector;
  label: string;
  options?: {
    value: string;
    label: string;
  }[];
} & TBaseField;

export type TPostcodeInputField = {
  fieldType: FieldType.PostcodeInput;
  label: string;
  dialogTitle: string;
  dialogDescription: string;
  primaryButtonTitle: string;
  secondaryButtonTitle: string;
  validation?: JSONSchemaType<unknown>;
  flex?: boolean;
} & TBaseField;

export type TDepositLimitSelection = {
  name: 'daily' | 'weekly' | 'monthly';
  label: string;
  inputLabel: string;
};

export type TIdentificationNumberESSelection = {
  documentDescription: string;
  name: string;
  labelInput: string;
  example: string;
};

export type TDepositLimitsField = {
  fieldType: FieldType.DepositLimits;
  name: string;
  selections: TDepositLimitSelection[];
  requiresOptInConfirmation?: boolean;
  noLimitOptionText?: string;
  setLimitOptionText?: string;
} & TBaseField;

export type TIdentificationNumberESField = {
  fieldType: FieldType.IdentificationNumberES;
  selections: TIdentificationNumberESSelection[];
  documentImage: string;
} & TBaseField;

export type TIdentityProtectedPasswordField = {
  fieldType: FieldType.IdentityProtectedPassword;
  validation?: JSONSchemaType<unknown>;
  label?: string;
  instructionPartOne?: string;
  instructionPartTwo?: TrustedHTML;
} & TBaseField;

export type TPasswordInputField = {
  fieldType: FieldType.PasswordInput;
  validation?: JSONSchemaType<unknown>;
  label?: string;
  ruleMinLength?: string;
  ruleHasLetter?: string;
  ruleHasNumber?: string;
  ruleHasSpecialChar?: string;
  successMessage?: string;
  errorMessage?: string;
  warningNote?: string;
  validationPanelTitle?: string;
} & TBaseField;

export type TEsPasswordInputField = {
  fieldType: FieldType.EsPasswordInput;
  validation?: JSONSchemaType<unknown>;
  label?: string;
  // Rule labels for checkmarks
  ruleMinLength?: string;
  ruleHasLetter?: string;
  ruleHasNumber?: string;
  ruleHasSpecialChar?: string;
  validationPanelTitle?: string;
  warningNote?: string;
  // Error messages for different states
  simplePatternError?: string;
  personalDetailsError?: string;
  patternAndPersonalError?: string;
  untickedRequirementsError?: string;
  successMessage?: string;
} & TBaseField;

export type TField =
  TCalloutField
  | TInputField
  | TCardField
  | TSelectorField
  | TTextField
  | TPhoneNumberInputField
  | TCountrySelectorField
  | TProvinceSelectorField
  | TSelectorDrawerField
  | TEnterAddressManuallyTextField
  | TEmailInputField
  | TAutoCompleteAddressInput
  | TWelcomeOfferField
  | TDateInputField
  | TConsentField
  | TWoCalloutField
  | TDepositIncrementerField
  | TDepositTypeSelectorField
  | TPostcodeInputField
  | TDepositLimitsField
  | TIdentificationNumberESField
  | TIdentityProtectedPasswordField
  | TPasswordInputField
  | TMultiMarketingConsentField
  | TEsPasswordInputField;

export type TButtonField = {
  redirectTo?: string;
  fieldType?: ButtonType;
  label?: string;
  gtmPayload?: Record<string, string>;
  name?: string;
};

export type TStep = {
  stepName?: string;
  name: string;
  fields: TField[];
  title?: string;
  description?: string;
  details?: string;
  primaryButton?: TButtonField;
  secondaryButton?: TButtonField;
  isStep?: boolean;
  navigationText?: string;
  currentStepText?: string;
  backgroundColor?: string;
  overflowingColor?: string;
  headerTextColor?: string;
  backgroundImage?: string;
  headerIcon?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TAdapterFunction = (values: TDynamicStoreField, market: string) => any;
export type TFlow<Step = TStep> = {
  name: string;
  adapter?: TAdapterFunction;
  steps: Record<number, Step>;
};

export type TLimit = {
  depositType: string;
  depositLimit: {
    value: number | string;
    currency: TCurrency;
  };
}

export type TUserRegistrationData = {
  email?: string;
  firstName?: string;
  lastName?: string;
  plainTextPassword?: string;
  phonePrefix?: string;
  phoneNumber?: string;
  phoneVerified?: boolean;
  socialSecurityNumber?: string;
  dayOfBirth?: number;
  monthOfBirth?: number;
  yearOfBirth?: number;
  gender?: string;
  address?: {
    country?: string;
    city?: string;
    postalCode?: string;
    streetName?: string;
    isVerified?: boolean;
    province?: string;
    houseNumber?: string;
  };
  nationality?: string;
  welcomeOfferId?: string;
  trackingId?: string;
  marketCode?: string;
  subscribeToMarketingMaterial?: boolean;
  marketingConsent?: {
    casino: {
      contactBySms: boolean;
      contactByEmail: boolean;
      contactByPhone: boolean;
      contactByPost: boolean;
      contactByPushNotification: boolean;
    };
    sports: {
      contactBySms: boolean;
      contactByEmail: boolean;
      contactByPhone: boolean;
      contactByPost: boolean;
      contactByPushNotification: boolean;
    };
  };
  limits?: Limits | null;
  limit?: TLimit | null;
  playerId?: string;
};

export type TRegistrationDoneGTM = {
  timeSpent?: number;
  numberOfOccasions?: number;
  email: string;
  countryRedirect?: boolean;
  countryResidence: string;
  wo_used: boolean;
  woUsed?: boolean;
  woId: string;
  wo_id: string;
  residentialAddress?: string;
  dailyDepositLimit: number | undefined;
  weeklyDepositLimit: number | undefined;
  monthlyDepositLimit: number | undefined;
  depositDefined?: boolean;
  depositAmount?: number | null;
  marketingConsent: MarketingConsent | boolean | undefined;
  marketing_consent: TMarketingConsentStatus;
  userId: string | undefined;
  // Temporary duplicate of userId, kept until GTM is migrated (PSUP-673)
  playerId: string | undefined;
  nationality: string;
  firstName?: string;
  lastName?: string;
};

export type TCookieOptions = {
  path?: string;
  expires?: number; // Number of days until the cookie expires
  secure?: boolean;
  sameSite?: 'lax' | 'strict' | 'none';
};

export type TBirthDate = {
  dayOfBirth: number;
  monthOfBirth: number;
  yearOfBirth: number;
};

export type TRegistrationResponse = {
  success: boolean;
  error?: string;
  data?: {
    playerId?: string;
  };
};

export type TDepositLimits = {
  minAllowedLimits: {
    monthly: number | null;
    weekly: number | null;
    daily: number | null;
  };
  maxAllowedLimits: {
    monthly: number | null;
    weekly: number | null;
    daily: number | null;
  };
};

export type TPlayOkLimits = {
  depositLimits: TDepositLimits;
};

export type TCurrency = 'SEK' | 'EUR' | 'CAD' | 'DKK' | 'NZD' | 'INR' | 'USD' | 'GBP';

export type TLimitType = 'DAILY' | 'WEEKLY' | 'MONTHLY';

export interface MarketingConsent {
  casino: {
    contactBySms: boolean;
    contactByEmail: boolean;
    contactByPhone: boolean;
    contactByPost: boolean;
    contactByPushNotification: boolean;
  };
  sports: {
    contactBySms: boolean;
    contactByEmail: boolean;
    contactByPhone: boolean;
    contactByPost: boolean;
    contactByPushNotification: boolean;
  };
}

export type TMarketingConsentStatus = 'granted' | 'partial' | 'denied';

export interface Limits {
  daily: {
    value: number | string;
    currency: TCurrency;
  };
  weekly: {
    value: number | string;
    currency: TCurrency;
  };
  monthly: {
    value: number | string;
    currency: TCurrency;
  };
}

export interface Limit {
  depositType: TLimitType;
  depositLimit: {
    value: number | string;
    currency: TCurrency;
  };
}
